import { useState, useEffect, useCallback, type Ref } from 'react'
import { Box, Flex, Text, VStack, Grid, Modal, ConfirmDialog, useT } from '@minetrace/ui'
import { useFormColors, FormSection, withFormProvider, useFormContext, Controller, FileInput, TrackedInput, TrackedDatePicker } from '@minetrace/ui/forms'
import {
  LuShieldCheck as ShieldCheck,
  LuFileText as FileText,
  LuPlus as Plus,
  LuTrash2 as Trash2,
} from 'react-icons/lu'
import TaxInfoSection from '../../../components/TaxInfoSection'
import type { BankAccountResponse, FileAttachmentResponse, TaxReportResponse } from '../../../api/models/response/proveedor'
import type { AddTaxReportRequest } from '../../../api/models/request/proveedor'
import type { UseFormReturn } from 'react-hook-form'
import { useStep8FinanceForm, type Step8FinanceFormValues } from './useStep8FinanceForm'
import { useStep8TaxReportForm, type TaxReportFormValues } from './useStep8TaxReportForm'
import DocumentoDescarga from '../../../components/DocumentoDescarga'
import { useDeleteWarning } from '../../../lib/modificationMode'

interface Step8FinanceProps {
  mode: 'edit' | 'readonly'
  // Bank accounts (migrated from TaxInfoSection)
  bankAccounts: BankAccountResponse[]
  onAddAccount: (data: { accountNumber: string; bankEntityId: number; accountTypeId: number }) => Promise<BankAccountResponse>
  onRemoveAccount: (accountId: string) => Promise<void>
  // Income tax (migrated from TaxInfoSection)
  incomeTaxCategoryId: number
  onIncomeTaxChange: (val: number) => void
  // New fields — now passed as defaults
  defaultSplaftDeclared?: boolean
  // Cambio de la declaración SPLAFT (usado en la solicitud de modificación para stagear)
  onSplaftDeclaredChange?: (value: boolean) => void
  // File upload for SPLAFT
  onSplaftFileChange?: (file: File | null) => void
  splaftDocumentFile?: FileAttachmentResponse | null
  // Tax reports (history)
  taxReports?: TaxReportResponse[]
  onAddTaxReport?: (data: AddTaxReportRequest, documentFile: File) => Promise<void>
  onRemoveTaxReport?: (reportId: string) => Promise<void>
  // Ref for parent to access form methods (trigger, getValues)
  ref?: Ref<unknown>
}

interface FinanceFormInnerProps {
  formMethods: ReturnType<typeof useStep8FinanceForm>
  mode: 'edit' | 'readonly'
  onSplaftDeclaredChange?: (value: boolean) => void
  onSplaftFileChange?: (file: File | null) => void
  splaftDocumentFile?: FileAttachmentResponse | null
}

const FinanceFormInnerRaw = ({
  mode,
  onSplaftDeclaredChange,
  onSplaftFileChange,
  splaftDocumentFile,
}: FinanceFormInnerProps) => {
  const c = useFormColors()
  const { t } = useT()
  const isReadonly = mode === 'readonly'
  const { watch, control } = useFormContext<Step8FinanceFormValues>()
  const [localSplaftFile, setLocalSplaftFile] = useState<File | null>(null)

  const splaftDeclared = watch('splaftDeclared')
  const { setValue, formState: { errors } } = useFormContext<Step8FinanceFormValues>()

  // Sync splaftDocumentUploaded with the external splaftDocumentFile prop OR local file selection
  useEffect(() => {
    setValue('splaftDocumentUploaded', Boolean(splaftDocumentFile) || Boolean(localSplaftFile))
  }, [splaftDocumentFile, localSplaftFile, setValue])

  const splaftDocError = (errors as Record<string, { message?: string }>).splaftDocumentUploaded?.message

  const handleFileChange = (file: File | null) => {
    setLocalSplaftFile(file)
    onSplaftFileChange?.(file)
  }

  return (
    <>
      {/* Section 8.2 — Declaracion Jurada SPLAFT (detraction accounts live now in TaxInfoSection as a bank account type) */}
      <FormSection icon={ShieldCheck} titleTid="proveedores.wizard.step8.splaftTitle" descriptionTid="proveedores.wizard.step8.splaftDescription">
        {/* Checkbox */}
        <Controller
          name="splaftDeclared"
          control={control}
          render={({ field, fieldState }) => (
            <Box>
              <Flex
                gap={3}
                alignItems="flex-start"
                p={4}
                bg={c.bgHover}
                borderRadius="sm"
                border={fieldState.error ? '1px solid' : '1px solid transparent'}
                borderColor={fieldState.error ? c.error : 'transparent'}
                cursor={isReadonly ? 'default' : 'pointer'}
                onClick={isReadonly ? undefined : () => { const next = !field.value; field.onChange(next); onSplaftDeclaredChange?.(next) }}
              >
                <Box
                  w="20px" h="20px" minW="20px" borderRadius="md" border="2px solid"
                  borderColor={field.value ? c.accent : 'gray.300'}
                  bg={field.value ? c.accent : 'transparent'}
                  display="flex" alignItems="center" justifyContent="center" mt={0.5}
                >
                  {field.value && (
                    <Text fontSize="xs" color="white" fontWeight="bold" lineHeight={1}>✓</Text>
                  )}
                </Box>
                <Text fontSize="sm" color={c.text}>
                  {t('proveedores.wizard.step8.splaftCheckboxLabel')} <Text as="span" color={c.error}>*</Text>
                </Text>
              </Flex>
              {fieldState.error && (
                <Text fontSize="xs" color={c.error} mt={1}>{fieldState.error.message}</Text>
              )}
            </Box>
          )}
        />

        {/* File upload — visible when declared */}
        {splaftDeclared && (
          <Box mt={4}>
            {splaftDocumentFile && (
              <Flex
                p={3}
                bg={`${c.accent}10`}
                borderRadius="lg"
                gap={2}
                alignItems="center"
                mb={3}
              >
                <DocumentoDescarga
                  caption="Declaración SPLAFT firmada"
                  storagePath={splaftDocumentFile.storagePath}
                  fileName={splaftDocumentFile.originalFileName}
                />
              </Flex>
            )}

            {!isReadonly && (
              <FileInput
                label={splaftDocumentFile ? t('proveedores.wizard.step8.splaftDocReplace') : t('proveedores.wizard.step8.splaftDocLabel')}
                required={!splaftDocumentFile}
                accept=".pdf,.jpg,.jpeg,.png"
                value={localSplaftFile}
                onChange={handleFileChange}
                error={splaftDocError}
                placeholder={t('proveedores.wizard.step8.splaftDocPlaceholder')}
              />
            )}

            {isReadonly && !splaftDocumentFile && (
              <Box p={4} textAlign="center" border="1px dashed" borderColor="gray.200" borderRadius="xl">
                <Text tid="proveedores.wizard.step8.splaftNoDocument" fontSize="sm" color={c.textMuted} />
              </Box>
            )}
          </Box>
        )}
      </FormSection>
    </>
  )
}

const FinanceFormInner = withFormProvider(FinanceFormInnerRaw, useStep8FinanceForm)

// ── Tax Reports Section ──────────────────────────────────────────────

interface TaxReportFormInnerProps {
  formMethods: UseFormReturn<TaxReportFormValues>
  isBusy: boolean
  onSave: (values: TaxReportFormValues, documentFile: File) => void
  onRegisterSubmit?: (fn: () => void) => void
}

const TaxReportFormInner = withFormProvider<TaxReportFormValues, TaxReportFormInnerProps>(
  ({ formMethods, isBusy, onSave, onRegisterSubmit }) => {
    const { t } = useT()
    const [documentFile, setDocumentFile] = useState<File | null>(null)
    const [fileError, setFileError] = useState<string | undefined>(undefined)

    const handleFormSubmit = useCallback(async () => {
      const isValid = await formMethods.trigger()
      if (!documentFile) {
        setFileError(t('proveedores.wizard.step8.taxReportDocRequired'))
      } else {
        setFileError(undefined)
      }
      if (!isValid || !documentFile) return
      onSave(formMethods.getValues(), documentFile)
    }, [formMethods, documentFile, onSave, t])

    useEffect(() => {
      onRegisterSubmit?.(() => handleFormSubmit())
    }, [onRegisterSubmit, handleFormSubmit])

    const today = new Date().toISOString().split('T')[0]

    return (
      <Box opacity={isBusy ? 0.5 : 1} pointerEvents={isBusy ? 'none' : 'auto'} transition="opacity 0.2s">
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
          <TrackedDatePicker name="issueDate" labelTid="proveedores.wizard.step8.taxReportIssueDateLabel" maxDate={today} />
          <TrackedInput name="code" labelTid="proveedores.wizard.step8.taxReportCodeLabel" placeholderTid="proveedores.wizard.step8.taxReportCodePlaceholder" maxLength={100} />
        </Grid>
        <Grid templateColumns="1fr" gap={4} mt={4}>
          <FileInput
            labelTid="proveedores.wizard.step8.taxReportDocLabel"
            required
            accept=".pdf"
            value={documentFile}
            onChange={(f) => { setDocumentFile(f); setFileError(undefined) }}
            error={fileError}
          />
        </Grid>
      </Box>
    )
  },
  useStep8TaxReportForm,
)

interface TaxReportsSectionProps {
  mode: 'edit' | 'readonly'
  taxReports: TaxReportResponse[]
  onAdd?: (data: AddTaxReportRequest, documentFile: File) => Promise<void>
  onRemove?: (reportId: string) => Promise<void>
}

const TaxReportsSection = ({ mode, taxReports, onAdd, onRemove }: TaxReportsSectionProps) => {
  const c = useFormColors()
  const { t } = useT()
  const isReadonly = mode === 'readonly' || !onAdd
  const [modalOpen, setModalOpen] = useState(false)
  const [isBusy, setIsBusy] = useState(false)
  const [submitFn, setSubmitFn] = useState<(() => void) | null>(null)
  const deleteWarning = useDeleteWarning()
  const [deleteTarget, setDeleteTarget] = useState<{ reportId: string; label: string } | null>(null)

  const handleSave = async (values: TaxReportFormValues, documentFile: File) => {
    if (!onAdd) return
    setIsBusy(true)
    try {
      await onAdd({ code: values.code || undefined, issueDate: values.issueDate }, documentFile)
      setModalOpen(false)
    } finally { setIsBusy(false) }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget || !onRemove) return
    setIsBusy(true)
    try { await onRemove(deleteTarget.reportId); setDeleteTarget(null) } finally { setIsBusy(false) }
  }

  return (
    <FormSection icon={FileText} titleTid="proveedores.wizard.step8.taxReportsTitle" descriptionTid="proveedores.wizard.step8.taxReportsDescription">
      {!isReadonly && (
        <Flex mb={4}>
          <Box as="button" type="button" display="flex" alignItems="center" gap={2}
               px={4} py={2} bg={c.accent} color="white" borderRadius="md" fontSize="sm" fontWeight="600"
               cursor="pointer" _hover={{ opacity: 0.9 }} onClick={() => setModalOpen(true)}>
            <Plus size={14} />{t('proveedores.wizard.step8.taxReportAddButton')}
          </Box>
        </Flex>
      )}

      {taxReports.length === 0 ? (
        <Box p={8} textAlign="center" borderRadius="xl" border="1px dashed" borderColor="gray.200">
          <FileText size={32} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
          <Text tid="proveedores.wizard.step8.taxReportEmpty" fontSize="sm" color={c.textMuted} />
        </Box>
      ) : (
        <Box overflowX="auto">
          <Box as="table" w="100%" fontSize="sm">
            <Box as="thead">
              <Box as="tr">
                {[
                  t('proveedores.wizard.step8.taxReportColCode'),
                  t('proveedores.wizard.step8.taxReportColIssue'),
                  t('proveedores.wizard.step8.taxReportColExpires'),
                  t('proveedores.wizard.step8.taxReportColStatus'),
                  t('proveedores.wizard.step8.taxReportColFile'),
                  ...(isReadonly ? [] : ['']),
                ].map(col => (
                  <Box as="th" key={col} textAlign="left" px={3} py={2} color={c.textMuted} fontWeight="600" fontSize="xs" borderBottom="1px solid" borderColor={c.border}>{col}</Box>
                ))}
              </Box>
            </Box>
            <Box as="tbody">
              {taxReports.map(r => (
                <Box as="tr" key={r.id} _hover={{ bg: c.bgHover }}>
                  <Box as="td" px={3} py={2} color={c.text}>{r.code ?? '—'}</Box>
                  <Box as="td" px={3} py={2} color={c.text}>{r.issueDate}</Box>
                  <Box as="td" px={3} py={2} color={c.text}>{r.expirationDate}</Box>
                  <Box as="td" px={3} py={2}>
                    <Box as="span" px={2} py={0.5} fontSize="xs" fontWeight="700" borderRadius="md"
                         bg={r.isValid ? 'green.100' : 'red.100'} color={r.isValid ? 'green.700' : 'red.700'}>
                      {r.isValid ? t('proveedores.wizard.step8.taxReportStatusValid') : t('proveedores.wizard.step8.taxReportStatusExpired')}
                    </Box>
                  </Box>
                  <Box as="td" px={3} py={2} color={c.text}>
                    {r.documentFile ? (
                      <DocumentoDescarga caption="Reporte tributario" storagePath={r.documentFile.storagePath} fileName={r.documentFile.originalFileName} />
                    ) : <Text tid="proveedores.wizard.step8.noFile" fontSize="sm" color={c.textMuted} />}
                  </Box>
                  {!isReadonly && (
                    <Box as="td" px={3} py={2}>
                      <Box as="button" type="button" display="flex" alignItems="center" gap={1} px={3} py={1.5}
                           bg="red.50" color="red.600" borderRadius="lg" fontSize="xs" fontWeight="600"
                           cursor="pointer" _hover={{ bg: 'red.100' }}
                           onClick={() => setDeleteTarget({ reportId: r.id, label: r.code ?? r.issueDate })}>
                        <Trash2 size={12} />{t('proveedores.wizard.step8.taxReportDelete')}
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t('proveedores.wizard.step8.taxReportModalTitle')}
        size="lg"
        footer={
          <>
            <Box as="button" type="button" px={5} py={2.5} borderRadius="md" fontSize="sm" fontWeight="600"
                 color={c.textMuted} border="1px solid" borderColor={c.border}
                 cursor="pointer" _hover={{ bg: c.bgHover }} onClick={() => setModalOpen(false)}>
              {t('proveedores.wizard.step8.taxReportCancel')}
            </Box>
            <Box as="button" type="button" display="flex" alignItems="center" gap={2}
                 px={6} py={2.5} bg={c.accent} color="white" borderRadius="md" fontSize="sm" fontWeight="600"
                 cursor={isBusy ? 'not-allowed' : 'pointer'} opacity={isBusy ? 0.6 : 1}
                 _hover={{ opacity: 0.9 }} onClick={() => submitFn?.()} disabled={isBusy}>
              {isBusy ? t('proveedores.wizard.step8.taxReportSaving') : <><Plus size={14} /> {t('proveedores.wizard.step8.taxReportRegister')}</>}
            </Box>
          </>
        }
      >
        <TaxReportFormInner
          isBusy={isBusy}
          onSave={handleSave}
          onRegisterSubmit={(fn) => setSubmitFn(() => fn)}
        />
      </Modal>

      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={t('proveedores.wizard.step8.taxReportDeleteTitle')}
        confirmLabel={t('proveedores.wizard.step8.taxReportDeleteConfirm')}
        variant="danger"
        isLoading={isBusy}
      >
        ¿Está seguro que desea eliminar el reporte tributario <Text as="span" fontWeight="600">{deleteTarget?.label}</Text>? {deleteWarning}
      </ConfirmDialog>
    </FormSection>
  )
}

const Step8Finance = ({
  ref,
  mode,
  bankAccounts,
  onAddAccount,
  onRemoveAccount,
  incomeTaxCategoryId,
  onIncomeTaxChange,
  defaultSplaftDeclared,
  onSplaftDeclaredChange,
  onSplaftFileChange,
  splaftDocumentFile,
  taxReports,
  onAddTaxReport,
  onRemoveTaxReport,
}: Step8FinanceProps) => {
  return (
    <VStack gap={6} alignItems="stretch">
      {/* Section 8.1 — Cuentas Bancarias & Impuesto a la Renta */}
      <TaxInfoSection
        mode={mode}
        bankAccounts={bankAccounts}
        incomeTaxCategoryId={incomeTaxCategoryId}
        onIncomeTaxChange={onIncomeTaxChange}
        onAddAccount={onAddAccount}
        onRemoveAccount={onRemoveAccount}
      />

      <FinanceFormInner
        ref={ref}
        mode={mode}
        onSplaftDeclaredChange={onSplaftDeclaredChange}
        onSplaftFileChange={onSplaftFileChange}
        splaftDocumentFile={splaftDocumentFile}
        defaultValues={{
          splaftDeclared: defaultSplaftDeclared ?? false,
        }}
      />

      {/* Section 8.3 — Reportes Tributarios (historial) */}
      <TaxReportsSection
        mode={mode}
        taxReports={taxReports ?? []}
        onAdd={onAddTaxReport}
        onRemove={onRemoveTaxReport}
      />
    </VStack>
  )
}

export default Step8Finance
